/* eslint-disable max-lines */
import React, { useEffect, useRef, useState } from 'react';
import ClassCard from 'aquilla/core/organisms/ClassCard';
import ShowCase from 'aquilla/core/molecules/ShowCase';
import styled from '@emotion/styled';
import { mediaQuery, Spacings } from '@styles/index';
import { useRouter } from 'next/router';
import pushRoute from '@utils/pushRoute';
import { getLink } from '@models/Item';
import { SEE_ALL_CLICKED } from '@constants/segment';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import Link from '@base/Link/Link';
import { useSnackbar } from '@stores/snackbar';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { notifyForUpcomingClass } from '@api/fetchers/class/updaters';
import { NOTIFY_ME, UN_NOTIFIED } from './event';
import { EducatorCardThumbnail } from './PopularClasses';

const UpcomingClassesContainer = styled.div`
  width: ${({ $width }) => $width ?? 'var(--layout-width)'};
  margin: ${Spacings.SPACING_12B} auto ${Spacings.SPACING_10B};

  ${mediaQuery.uptoTablet} {
    width: 100%;
    margin: ${Spacings.SPACING_8B} auto ${Spacings.SPACING_10B};
  }

  ${mediaQuery.onlyTablet} {
    .aquilla-carousel-wrapper > *:first-child {
      margin-left: ${Spacings.SPACING_8B};
    }
    .aquilla-carousel-wrapper > *:last-child {
      margin-right: ${Spacings.SPACING_8B};
    }
    .showcase-section-header {
      padding: 0 ${Spacings.SPACING_8B};
    }
  }

  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin: ${Spacings.SPACING_8B} auto ${Spacings.SPACING_8B};
    padding: 0;
  }
`;

const UpcomingClasses = ({
  block,
  showUpcomingClass,
  extraInfo,
  revalidateSubscribedBlocks
}) => {
  const classes = block.courses || block.data;
  const { extraBlockInfo } = block;
  const {
    pathname,
    query: { goalSlug, goalUID }
  } = useRouter();
  const goalSegmentData = useGoalEventProps({});
  const { open: showSnackbar } = useSnackbar();
  const isRequestingEnroll = useRef(false);
  const [classData, setClassData] = useState(null);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);

  const onNotifyClick = async (e, classInfo) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!isLoggedIn) {
      isRequestingEnroll.current = true;
      setClassData(classInfo);
      openLoginDrawer();
    } else {
      try {
        const { isEnrolled } = classInfo;
        const response = await notifyForUpcomingClass({
          programmeUid: classInfo.uid,
          enroll: !isEnrolled
        });
        if (!response.error) {
          segment.track(isEnrolled ? UN_NOTIFIED : NOTIFY_ME, {
            ...goalSegmentData,
            last_primary_source_section:
              'Free Live Class Batch Group Touch Point',
            last_primary_source: extraInfo?.lps ?? 'Goal Home'
          });
          const message = isEnrolled
            ? 'You’ve unsubscribed class updates'
            : 'We’ll notify you when the class is live';
          showSnackbar({
            message,
            type: isEnrolled ? 'info' : 'success',
            anchorOrigin: { vertical: 'top', horizontal: 'center' }
          });
          await revalidateSubscribedBlocks();
        } else {
          showSnackbar({
            message: 'Something went wrong. Please try again.',
            type: 'error',
            anchorOrigin: { vertical: 'top', horizontal: 'center' }
          });
        }
      } finally {
        isRequestingEnroll.current = false;
        setClassData(null);
      }
    }
  };
  useEffect(() => {
    if (isLoggedIn && isRequestingEnroll && classData)
      onNotifyClick(null, classData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, classData, showUpcomingClass]);

  if (!showUpcomingClass) return null;
  const handleSeeAll = (e) => {
    e.preventDefault();
    segment.track(SEE_ALL_CLICKED, {
      ...goalSegmentData,
      last_primary_source_section: 'Upcoming Classes',
      last_primary_source: extraInfo?.lps ?? 'Goal Home'
    });
    const route = {
      href: '/goal/[goalSlug]/[goalUID]/classes/upcoming',
      as: `/goal/${goalSlug}/${goalUID}/classes/upcoming`
    };
    pushRoute(route.href, route.as);
  };
  return (
    <UpcomingClassesContainer $width={extraInfo?.width}>
      <ShowCase
        title={extraBlockInfo?.header || extraInfo?.title}
        description={extraInfo?.description ?? ''}
        itemsPerSlide={extraInfo?.itemsPerSlide || 4}
        onSeeAll={handleSeeAll}
        hideArrowButtonOnTablet
        seeAllVariant={
          pathname === '/goal/[goalSlug]/[goalUID]'
            ? 'ghost-green'
            : 'secondary'
        }
      >
        {classes.map((classInfo) => {
          const as = getLink({
            ...classInfo,
            nextTrack: {
              ...classInfo.nextSession,
              ...classInfo.nextSession?.properties
            }
          });
          return (
            <Link passHref key={classInfo.uid} href={as}>
              <ClassCard
                as="div"
                showNotifyButton
                onNotify={(e) => onNotifyClick(e, classInfo)}
                notified={classInfo.isEnrolled}
                educatorName={classInfo.educatorName}
                educatorLink={classInfo.educatorLink}
                title={classInfo.title}
                topicName={classInfo.topicName}
                topicColor={classInfo.topicColor}
                langLabel={classInfo.langLabel}
                viewCount={classInfo.viewCount}
                detailsLine={classInfo.detailsLine}
                variant="Upcoming"
                thumbnail={() => (
                  <EducatorCardThumbnail
                    coverPhoto={classInfo.coverPhotoV1}
                    topicColor={classInfo.topicColor}
                  />
                )}
              />
            </Link>
          );
        })}
      </ShowCase>
    </UpcomingClassesContainer>
  );
};

export default UpcomingClasses;
